declare const L: any;

import { $ } from '../ui/dom';
import { showToast } from '../ui/toast';
import { deviceDisplayName } from '../ui/animals';
import { getAuthUserId, setAuthUserId, getAuthReady } from '../state/auth-state';
import { COHORT_PALETTE, colorForToken } from '../state/cohort-model';
import { loadDeviceHistory } from '../storage/persistence';
import { apiGetWorldActive } from '../api/world';
import { apiAuthMe } from '../api/claim';

let worldMap: any = null;
let worldMarkers: any = null;          // L.layerGroup holding the per-fetch markers
let worldPollTimer: any = null;        // 30 s background poll
let worldMoveTimer: any = null;        // debounce for moveend → refetch
let worldInFlight = false;             // single-flight guard so poll + move don't stack

export function getWorldMap(): any { return worldMap; }
export function getWorldPollTimer(): any { return worldPollTimer; }

// Above this, a precision is a non-fix, not a fuzzy area (see tracker.ts for
// the same constant). Keep the two values in sync with each other and with
// Android TrailMap.kt MAX_ACCURACY_CIRCLE_M.
const MAX_ACCURACY_CIRCLE_M = 30_000;

export function ensureWorldMap(){
  if(worldMap){ worldMap.invalidateSize(); return; }
  worldMap = L.map('world-map', { zoomControl:true, attributionControl:true, scrollWheelZoom:true })
    .setView([20, 0], 2);
  // Same CartoDB light tile config as the tracker map (ensureMap).
  L.tileLayer('https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png', {
    subdomains:'abcd', attribution:'© OpenStreetMap · CartoDB', maxZoom: 19,
  }).addTo(worldMap);
  worldMarkers = L.layerGroup().addTo(worldMap);
  // Debounced refetch on pan/zoom — a fresh viewport is a fresh bbox query.
  worldMap.on('moveend', () => {
    if(worldMoveTimer) clearTimeout(worldMoveTimer);
    worldMoveTimer = setTimeout(() => { void fetchWorld(); }, 400);
  });
}

// Normalize worldMap.getBounds() into a bbox the endpoint accepts. Leaflet can
// return lng outside [-180,180] (a wrapped/over-panned world view) and w > e
// when the view straddles the antimeridian — the endpoint 400s both. Clamp to
// [-180,180]; if it still wraps (w > e) widen to the full longitude span so a
// wrapped view degrades to "show the whole world" instead of erroring.
export function worldBbox(){
  const b = worldMap.getBounds();
  const n = Math.min(90, Math.max(-90, b.getNorth()));
  const s = Math.min(90, Math.max(-90, b.getSouth()));
  let e = Math.min(180, Math.max(-180, b.getEast()));
  let w = Math.min(180, Math.max(-180, b.getWest()));
  if(w > e){ w = -180; e = 180; }
  return { n, s, e, w };
}

export function setWorldSignedOut(on: boolean){
  const so = $('world-signedout');
  if(so) so.hidden = !on;
  const status = $('world-status');
  if(status) status.textContent = on ? 'sign in to view' : 'live · public devices worldwide';
}

export async function fetchWorld(){
  if(!worldMap || !$('screen-world').classList.contains('is-active')) return;
  if(document.hidden) return;          // F1: skip hidden-tab ticks (re-show catches up)
  // Session-required. The first poll fires ~60ms after the screen shows, which
  // can BEAT the boot auth probe (initAuth) — so wait for that to settle, then
  // fall back to a direct probe, before deciding signed-out. Critically we do
  // NOT stopWorldLive() on a signed-out / 401 result: the poll stays alive so a
  // later sign-in (or an again-valid session) self-heals within one interval.
  // Permanently stopping here was the bug where an already-signed-in user whose
  // first poll lost the race to initAuth got stuck on the sign-in overlay.
  if(getAuthUserId() == null){ try { await getAuthReady(); } catch { /* ignore */ } }
  if(getAuthUserId() == null){ try { setAuthUserId(await apiAuthMe()); } catch { setAuthUserId(null); } }
  // The awaits above can outlast the screen — bail if the user navigated away.
  if(!$('screen-world').classList.contains('is-active')) return;
  if(getAuthUserId() == null){ setWorldSignedOut(true); return; }
  setWorldSignedOut(false);
  if(worldInFlight) return;
  worldInFlight = true;
  try {
    const { n, s, e, w } = worldBbox();
    const { ok, status, snap } = await apiGetWorldActive(n, s, e, w);
    if(status === 401){ setWorldSignedOut(true); setAuthUserId(null); return; }
    if(status === 429){ showToast('world map rate limited — slow down'); return; }
    if(!ok){ console.warn('[locus] world fetch', status); return; }
    renderWorldDevices(snap!.devices);
    const hint = $('world-truncated');
    if(hint) hint.hidden = !snap!.truncated;
  } catch (err) {
    console.warn('[locus] world fetch failed', err);
  } finally {
    worldInFlight = false;
  }
}

export function renderWorldDevices(devices: { id:string; animals:string[]; visibility:string; mine:boolean; lat:number; lng:number; t:number; precision_meters:number|null; tag:string|null }[]){
  if(!worldMarkers) return;
  worldMarkers.clearLayers();
  const history = loadDeviceHistory();   // F3: parse once for the whole pass
  for(const d of devices){
    // Own devices (Set C) take the reserved accent (#B84A22, palette index 0);
    // every other device hashes to a stable non-accent colour keyed on the
    // opaque cohort id.
    const color = d.mine ? COHORT_PALETTE[0] : colorForToken(d.id);
    // A coarsened (group) device carries a precision_meters — draw it as a
    // fuzzy area so it doesn't read as a precise point. Cap at
    // MAX_ACCURACY_CIRCLE_M like the session/share maps: above it the value is
    // a non-fix (a ~357 km garbage GPS precision), which would wash the whole
    // world map in one faint circle rather than read as a fuzzy area.
    if(d.precision_meters != null && d.precision_meters > 1
       && d.precision_meters <= MAX_ACCURACY_CIRCLE_M){
      L.circle([d.lat, d.lng], { radius: d.precision_meters, color, weight: 1, opacity: 0.3, fillOpacity: 0.06 })
        .addTo(worldMarkers);
    }
    const name = deviceDisplayName(d.id, d.animals, null, history, d.tag);
    L.circleMarker([d.lat, d.lng], {
      radius: 6, color, weight: 2, fillColor: color, fillOpacity: 0.85,
    })
      .bindTooltip(name, { direction: 'top' })
      .addTo(worldMarkers);
  }
}

export function startWorldLive(){
  stopWorldLive();
  void fetchWorld();
  worldPollTimer = setInterval(() => { void fetchWorld(); }, 30_000);
}

export function stopWorldLive(){
  if(worldPollTimer){ clearInterval(worldPollTimer); worldPollTimer = null; }
  if(worldMoveTimer){ clearTimeout(worldMoveTimer); worldMoveTimer = null; }
}
